import { cn } from "@/utils/theme";
import { Badge, Icon } from "@elaniintc/goes-design-system";
import {
  getAvailableDay,
  getEventsForDate,
  weekDays,
  type DayType,
  type EventForDay,
  type EventType,
} from "../utils/utils";
import EventBadge from "./event";

interface CalendarTableProps {
  currentDate: Date;
  daysEnable?: DayType[];
  rows: Array<Date[]>;
  eventsData: EventType[];
  openModalDay: (
    date: Date,
    isAvailable: boolean,
    hasEventBlocked: boolean,
    events: EventForDay[],
  ) => void;
}

const CalendarTable = ({
  currentDate,
  daysEnable = ["SAB", "DOM"],
  rows,
  eventsData,
  openModalDay,
}: CalendarTableProps) => {
  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const renderCell = (date: Date, isLast: boolean) => {
    const events = getEventsForDate(eventsData, date);
    const isAvailable = !daysEnable?.includes(getAvailableDay(date));
    const hasEventBlocked =
      events.filter((e) =>
        ["HOLIDAY", "NON_WORKING_DAY", "WEEKEND"].includes(e.type),
      ).length > 0;

    return (
      <td
        key={`cell-${date.getDate()}-${date.getMonth()}-${date.getFullYear()}`}
        onClick={() => openModalDay(date, isAvailable, hasEventBlocked, events)}
        data-testid="open-day"
        className={cn(
          "p-1 h-27.5 block cursor-pointer",
          !isLast && "border-r",
          "border-neutral-silent",
          isAvailable && "bg-neutral-object",
        )}
      >
        <div className="flex flex-col h-full">
          <div className="p-1 flex justify-between items-center">
            {renderDayButton(date)}
            <span
              className={cn(
                "text-xs",
                isAvailable && !hasEventBlocked
                  ? "text-action"
                  : "text-on-disabled",
              )}
            >
              {isAvailable && !hasEventBlocked ? "Hábil" : "No Hábil"}
            </span>
          </div>

          <div className="mt-1 flex flex-col overflow-hidden">
            {events.length > 0 &&
              events
                .slice(0, 2)
                .map((event, index) => (
                  <EventBadge key={event.id || index} event={event} />
                ))}

            {events.length > 2 && (
              <div className="cursor-pointer text-body-quiet flex items-center justify-between text-xs rounded-md h-5 overflow-hidden">
                <div className="flex items-center gap-1 flex-1 min-w-0 px-2">
                  <span className="truncate flex-1">
                    +{events.length - 2} eventos
                  </span>
                </div>

                <div className="px-1 h-full flex items-center">
                  <Icon type="far" name="arrow-up-right" size="xs" />
                </div>
              </div>
            )}
          </div>
        </div>
      </td>
    );
  };

  const renderDayButton = (date: Date) => {
    return (
      <button
        type="button"
        data-outside={!isCurrentMonth(date)}
        data-today={isToday(date)}
        className={cn(
          "size text-body-loud font-geist-mono rounded-full cursor-pointer",
          "text-xs leading-2xl font-normal",
          "data-[today=true]:bg-surface-action-solid",
          "data-[today=true]:text-white",
          "data-[today=true]:px-1",
          "data-[outside=true]:text-on-disabled",
        )}
      >
        {date.getDate()}
      </button>
    );
  };

  return (
    <div className="flex-1 overflow-hidden">
      <table className="w-full h-full">
        <thead>
          <tr className="grid grid-cols-7 border-b border-neutral-silent bg-neutral-object-accent-silent">
            {weekDays.map((day, index) => {
              const isLast = index === weekDays.length - 1;
              const isEnableDay = daysEnable.includes(day);
              return (
                <th
                  key={day}
                  className={cn(
                    "col-span-1 h-8 flex justify-between items-center p-1 border-neutral-silent",
                    !isLast && "border-r",
                  )}
                >
                  <span
                    className="text-xs text-body"
                    data-testid={isEnableDay ? "days-enable" : null}
                  >
                    {day}
                  </span>
                  {isEnableDay && (
                    <Badge
                      data-testid="weekend-badge"
                      size="xs"
                      shape="square"
                      color="neutral"
                    >
                      Fin de semana
                    </Badge>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody className="grid h-full grid-rows-5">
          {rows.map((week, wId) => {
            const isLast = wId === rows.length - 1;
            return (
              <tr
                key={wId}
                data-testid="calendar-row"
                className={cn(
                  "grid grid-cols-7 border-neutral-silent",
                  !isLast && "border-b",
                )}
              >
                {week.map((date, dId) => {
                  const isLastCell = dId === week.length - 1;
                  return renderCell(date, isLastCell);
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CalendarTable;
